import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";

interface SiteConfig {
  marquee: { text: string; enabled: boolean };
  language: { show_switch: boolean };
  pricing: { show_prices: boolean };
  site: { site_name: string; tagline: string; copyright: string; footer_text: string };
  notification: { email: string; from_email: string };
  social: { facebook: string; instagram: string; twitter: string; whatsapp: string; whatsapp_enabled: boolean };
  contactDisplay: {
    phone_footer: boolean;
    phone_contact: boolean;
    phone_part_request: boolean;
    phone_contact_cta: boolean;
    email_footer: boolean;
    email_contact: boolean;
    address_footer: boolean;
    address_contact: boolean;
  };
  seo: {
    meta_title: string;
    meta_description: string;
    meta_keywords: string;
    canonical_url: string;
    og_title: string;
    og_description: string;
    og_image: string;
    robots: string;
  };
  ads: { enabled: boolean; conversion_id: string; script: string };
}

interface ContactDetail {
  id: string;
  type: string;
  value: string;
  label: string | null;
  sort_order: number;
}

const CONTACT_TYPES = ["phone", "email", "address"];

export default function SettingsManager() {
  const [config, setConfig] = useState<SiteConfig>({
    marquee: { text: "", enabled: true },
    language: { show_switch: true },
    pricing: { show_prices: true },
    site: { site_name: "Top Gear Auto Spares", tagline: "", copyright: "", footer_text: "" },
    notification: { email: "", from_email: "sales@topgearautospares.com" },
    social: { facebook: "", instagram: "", twitter: "", whatsapp: "", whatsapp_enabled: true },
    contactDisplay: {
      phone_footer: true,
      phone_contact: true,
      phone_part_request: true,
      phone_contact_cta: true,
      email_footer: true,
      email_contact: true,
      address_footer: true,
      address_contact: true,
    },
    seo: {
      meta_title: "Top Gear Auto Spares | Used Engines, Transmissions & Parts",
      meta_description: "Tested used engines, transmissions and body parts from Top Gear Auto Spares, Darwin. Quality second-hand car parts shipped Australia-wide with warranty.",
      meta_keywords: "used engines, used transmissions, auto body parts, second hand car parts, Darwin auto spares",
      canonical_url: "https://topgearautospares.com/",
      og_title: "Top Gear Auto Spares | Used Engines, Transmissions & Parts",
      og_description: "Tested used engines, transmissions and body parts from Top Gear Auto Spares, Darwin. Quality second-hand car parts shipped Australia-wide with warranty.",
      og_image: "",
      robots: "index, follow",
    },
    ads: { enabled: false, conversion_id: "", script: "" },
  });
  const [contacts, setContacts] = useState<ContactDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [newContact, setNewContact] = useState({ type: "phone", value: "", label: "" });
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: settingsData } = await supabase.from("site_settings").select("key, value");
      const next = { ...config };
      (settingsData || []).forEach((row) => {
        const k = row.key as keyof SiteConfig;
        if (k in next) {
          // Merge so newly added fields (e.g. from_email, whatsapp) keep their
          // defaults even when the saved row predates them.
          (next as unknown as Record<string, unknown>)[k] = {
            ...((next as unknown as Record<string, unknown>)[k] as Record<string, unknown>),
            ...(row.value as Record<string, unknown>),
          };
        }
      });
      setConfig(next);

      const { data: contactsData } = await supabase
        .from("contact_details")
        .select("*")
        .is("deleted_at", null)
        .order("sort_order", { ascending: true });
      setContacts((contactsData as ContactDetail[]) || []);
    } catch {
      setMessage({ type: "error", text: "Failed to load settings." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const rows = Object.entries(config).map(([key, value]) => ({
      key,
      value,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase.from("site_settings").upsert(rows);
    setSaving(false);
    setMessage(
      error
        ? { type: "error", text: error.message }
        : { type: "success", text: "Settings saved successfully." }
    );
  };

  const addContact = async () => {
    if (!newContact.value.trim()) return;
    const sortOrder = contacts.filter((c) => c.type === newContact.type).length + 1;
    const { error } = await supabase.from("contact_details").insert({
      type: newContact.type,
      value: newContact.value.trim(),
      label: newContact.label.trim() || null,
      sort_order: sortOrder,
    });
    if (error) {
      setMessage({ type: "error", text: error.message });
      return;
    }
    setNewContact({ type: newContact.type, value: "", label: "" });
    setMessage({ type: "success", text: "Contact detail added." });
    load();
  };

  const removeContact = async (id: string) => {
    const { error } = await supabase
      .from("contact_details")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      setMessage({ type: "error", text: error.message });
      return;
    }
    setMessage({ type: "success", text: "Contact detail moved to Trash." });
    load();
  };

  const setNested = <K extends keyof SiteConfig>(section: K, field: string, value: unknown) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  // Persist a single setting immediately so toggles (price / marquee / language)
  // take effect on the public site without needing a separate "Save" click.
  const toggleAndSave = async <K extends keyof SiteConfig>(
    section: K,
    field: string,
    current: boolean
  ) => {
    const nextValue = { ...config[section], [field]: !current };
    setNested(section, field, !current);
    setMessage(null);
    try {
      const { error } = await supabase
        .from("site_settings")
        .upsert({ key: section, value: nextValue, updated_at: new Date().toISOString() });
      setMessage(
        error
          ? { type: "error", text: error.message }
          : { type: "success", text: "Setting updated — live on the site now." }
      );
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to update setting.",
      });
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-enquiry-email", {
        body: {
          test: true,
          name: "Test Sender",
          email: testEmail.trim(),
          subject: "Email delivery test",
          notes: "This is a test message to confirm your email notifications are working.",
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setMessage({
        type: "success",
        text: "Test email sent. Check the admin inbox and the test address above.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to send test email.",
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
      </div>
    );
  }

  const contactTypeLabel = (t: string) =>
    t === "phone" ? "Phone" : t === "email" ? "Email" : "Address";

  const displayToggles: { key: keyof SiteConfig["contactDisplay"]; label: string }[] = [
    { key: "phone_footer", label: "Phone — Footer" },
    { key: "phone_contact", label: "Phone — Contact page" },
    { key: "phone_part_request", label: "Phone — 'Can't find your part' banner" },
    { key: "phone_contact_cta", label: "Phone — 'Looking for a part' section" },
    { key: "email_footer", label: "Email — Footer" },
    { key: "email_contact", label: "Email — Contact page" },
    { key: "address_footer", label: "Address — Footer" },
    { key: "address_contact", label: "Address — Contact page" },
  ];

  return (
    <div>
      <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">Site Settings</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Manage your banner, language switcher, footer text and contact details.
      </p>

      {message && (
        <div
          className={`mt-4 flex items-center justify-between rounded-md border px-4 py-3 ${
            message.type === "success"
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <span className="text-sm">{message.text}</span>
          <button onClick={() => setMessage(null)}>
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      <form onSubmit={saveSettings} className="mt-5 space-y-6">
        {/* Sale banner */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
            Sale Banner (Marquee)
          </h3>
          <div className="mt-4 flex items-center justify-between rounded-md bg-neutral-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-brand-dark">Enable banner</p>
              <p className="text-xs text-neutral-500">Show the scrolling sale banner at the top of the site.</p>
            </div>
            <button
              type="button"
              onClick={() => toggleAndSave("marquee", "enabled", config.marquee.enabled)}
              className={`relative h-6 w-11 rounded-full transition ${
                config.marquee.enabled ? "bg-brand-gold" : "bg-neutral-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                  config.marquee.enabled ? "left-[22px]" : "left-0.5"
                }`}
              ></span>
            </button>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-brand-dark">Banner Text</label>
            <textarea
              value={config.marquee.text}
              onChange={(e) => setNested("marquee", "text", e.target.value)}
              rows={2}
              className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
            />
          </div>
        </section>

        {/* Language switcher */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
            Language Switcher
          </h3>
          <div className="mt-4 flex items-center justify-between rounded-md bg-neutral-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-brand-dark">Show language icon</p>
              <p className="text-xs text-neutral-500">
                Display the globe icon in the navbar for switching between languages.
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleAndSave("language", "show_switch", config.language.show_switch)}
              className={`relative h-6 w-11 rounded-full transition ${
                config.language.show_switch ? "bg-brand-gold" : "bg-neutral-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                  config.language.show_switch ? "left-[22px]" : "left-0.5"
                }`}
              ></span>
            </button>
          </div>
        </section>

        {/* Pricing visibility */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">Pricing</h3>
          <div className="mt-4 flex items-center justify-between rounded-md bg-neutral-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-brand-dark">Show prices</p>
              <p className="text-xs text-neutral-500">
                Toggle off to hide all prices on the public site, including product cards and enquiry forms.
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleAndSave("pricing", "show_prices", config.pricing.show_prices)}
              className={`relative h-6 w-11 rounded-full transition ${
                config.pricing.show_prices ? "bg-brand-gold" : "bg-neutral-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                  config.pricing.show_prices ? "left-[22px]" : "left-0.5"
                }`}
              ></span>
            </button>
          </div>
        </section>

        {/* Site info */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">Site Information</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-brand-dark">Site Name</label>
              <input
                value={config.site.site_name}
                onChange={(e) => setNested("site", "site_name", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Tagline</label>
              <input
                value={config.site.tagline}
                onChange={(e) => setNested("site", "tagline", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-brand-dark">Footer Text</label>
              <textarea
                value={config.site.footer_text}
                onChange={(e) => setNested("site", "footer_text", e.target.value)}
                rows={2}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-brand-dark">Copyright Text</label>
              <input
                value={config.site.copyright}
                onChange={(e) => setNested("site", "copyright", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
          </div>
        </section>

        {/* Notification + social */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
            Notifications & Social
          </h3>
          <div className="mt-4 flex items-center justify-between rounded-md bg-neutral-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-brand-dark">Show WhatsApp button</p>
              <p className="text-xs text-neutral-500">
                Display the floating WhatsApp chat button on the public site.
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleAndSave("social", "whatsapp_enabled", config.social.whatsapp_enabled)}
              className={`relative h-6 w-11 rounded-full transition ${
                config.social.whatsapp_enabled ? "bg-brand-gold" : "bg-neutral-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                  config.social.whatsapp_enabled ? "left-[22px]" : "left-0.5"
                }`}
              ></span>
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-brand-dark">Notification Email</label>
              <input
                type="email"
                value={config.notification.email}
                onChange={(e) => setNested("notification", "email", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
              <p className="mt-1 text-xs text-neutral-400">
                Where new enquiries and notifications are sent.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">From Email (sending address)</label>
              <select
                value={config.notification.from_email}
                onChange={(e) => setNested("notification", "from_email", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              >
                {Array.from(
                  new Set(
                    [
                      "sales@topgearautospares.com",
                      config.notification.from_email,
                      config.notification.email,
                      ...contacts.filter((c) => c.type === "email").map((c) => c.value),
                    ].filter(Boolean)
                  )
                ).map((addr) => (
                  <option key={addr} value={addr}>
                    {addr}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-400">
                The address emails are sent from. Must belong to your verified Resend domain.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Facebook</label>
              <input
                value={config.social.facebook}
                onChange={(e) => setNested("social", "facebook", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Instagram</label>
              <input
                value={config.social.instagram}
                onChange={(e) => setNested("social", "instagram", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Twitter / X</label>
              <input
                value={config.social.twitter}
                onChange={(e) => setNested("social", "twitter", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">WhatsApp Number</label>
              <input
                type="tel"
                value={config.social.whatsapp}
                onChange={(e) => setNested("social", "whatsapp", e.target.value)}
                placeholder="+61 4xx xxx xxx"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
              <p className="mt-1 text-xs text-neutral-400">
                Shown on the floating WhatsApp button across the site. Include the country code (e.g. +61).
              </p>
            </div>
          </div>

          <div className="mt-5 border-t border-neutral-100 pt-4">
            <p className="text-sm font-medium text-brand-dark">Email Delivery Test</p>
            <p className="text-xs text-neutral-500">
              Send a test email to confirm notifications reach both your admin inbox and a customer address.
            </p>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Test recipient (e.g. you@email.com)"
                className="flex-1 rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
              <button
                type="button"
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="flex items-center justify-center gap-2 rounded-md bg-brand-dark px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-black disabled:opacity-60 whitespace-nowrap"
              >
                {sendingTest ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-mail-send-line"></i>
                )}
                {sendingTest ? "Sending…" : "Send Test Email"}
              </button>
            </div>
          </div>
        </section>

        {/* Contact display */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
            Contact Display
          </h3>
          <p className="mt-1 text-xs text-neutral-500">
            Choose where your phone numbers, emails and addresses appear on the public site. Turn any off to hide it from that section.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {displayToggles.map((item) => (
              <div
                key={item.key}
                className="flex items-center justify-between gap-4 rounded-md bg-neutral-50 px-4 py-3"
              >
                <p className="text-sm font-medium text-brand-dark">{item.label}</p>
                <button
                  type="button"
                  onClick={() =>
                    toggleAndSave("contactDisplay", item.key, config.contactDisplay[item.key])
                  }
                  className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                    config.contactDisplay[item.key] ? "bg-brand-gold" : "bg-neutral-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                      config.contactDisplay[item.key] ? "left-[22px]" : "left-0.5"
                    }`}
                  ></span>
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* SEO */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
                SEO &amp; Metadata
              </h3>
              <p className="mt-1 text-xs text-neutral-500">
                Set the title, description and keywords used for search engine ranking.
              </p>
            </div>
            <i className="ri-search-eye-line text-2xl text-brand-gold"></i>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-brand-dark">Page Title</label>
              <input
                value={config.seo.meta_title}
                onChange={(e) => setNested("seo", "meta_title", e.target.value)}
                placeholder="Top Gear Auto Spares | Used Engines & Transmissions"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Keywords</label>
              <input
                value={config.seo.meta_keywords}
                onChange={(e) => setNested("seo", "meta_keywords", e.target.value)}
                placeholder="used engines, transmissions, auto parts Australia"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-brand-dark">Meta Description</label>
              <textarea
                value={config.seo.meta_description}
                onChange={(e) => setNested("seo", "meta_description", e.target.value)}
                rows={2}
                maxLength={160}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
              <p className="mt-1 text-xs text-neutral-400">{config.seo.meta_description.length}/160</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Canonical URL</label>
              <input
                value={config.seo.canonical_url}
                onChange={(e) => setNested("seo", "canonical_url", e.target.value)}
                placeholder="https://topgearautospares.com/"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Robots</label>
              <input
                value={config.seo.robots}
                onChange={(e) => setNested("seo", "robots", e.target.value)}
                placeholder="index, follow"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">OG Title (social share)</label>
              <input
                value={config.seo.og_title}
                onChange={(e) => setNested("seo", "og_title", e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-brand-dark">OG Image (social share preview)</label>
              <div className="mt-1.5 flex items-start gap-4">
                <div className="flex-1">
                  <input
                    value={config.seo.og_image}
                    onChange={(e) => setNested("seo", "og_image", e.target.value)}
                    placeholder="https://…/share-image.jpg"
                    className="w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-neutral-400">
                    Recommended 1200×630px (JPG/PNG). Shown when your link is shared on Facebook, WhatsApp and LinkedIn.
                  </p>
                </div>
                {config.seo.og_image ? (
                  <img
                    src={config.seo.og_image}
                    alt="Social share preview"
                    className="h-20 w-40 shrink-0 rounded-md border border-neutral-200 object-cover"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <div className="flex h-20 w-40 shrink-0 items-center justify-center rounded-md border border-dashed border-neutral-300 bg-neutral-50 text-xs text-neutral-400">
                    No image
                  </div>
                )}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-brand-dark">OG Description</label>
              <textarea
                value={config.seo.og_description}
                onChange={(e) => setNested("seo", "og_description", e.target.value)}
                rows={2}
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
          </div>
        </section>

        {/* Google Ads */}
        <section className="rounded-lg border border-neutral-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
                Google Ads
              </h3>
              <p className="mt-1 text-xs text-neutral-500">
                Run Google Ads on your site. Paste your conversion / tag ID and any extra tracking snippet.
              </p>
            </div>
            <i className="ri-advertisement-line text-2xl text-brand-gold"></i>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-md bg-neutral-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-brand-dark">Enable Google Ads</p>
              <p className="text-xs text-neutral-500">
                Inject the Google tag and tracking scripts on the public site.
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleAndSave("ads", "enabled", config.ads.enabled)}
              className={`relative h-6 w-11 rounded-full transition ${
                config.ads.enabled ? "bg-brand-gold" : "bg-neutral-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                  config.ads.enabled ? "left-[22px]" : "left-0.5"
                }`}
              ></span>
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-dark">
                Google Tag / Conversion ID
              </label>
              <input
                value={config.ads.conversion_id}
                onChange={(e) => setNested("ads", "conversion_id", e.target.value)}
                placeholder="AW-123456789"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
              <p className="mt-1 text-xs text-neutral-400">
                Found in your Google Ads account under Tools &gt; Conversions.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">
                Extra Tracking Script (optional)
              </label>
              <textarea
                value={config.ads.script}
                onChange={(e) => setNested("ads", "script", e.target.value)}
                rows={3}
                placeholder="Paste any additional Google Ads / remarketing snippet here."
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
          </div>
        </section>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
        >
          {saving && <i className="ri-loader-4-line animate-spin"></i>}
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>

      {/* Contact details */}
      <section className="mt-8 rounded-lg border border-neutral-200 bg-white p-5">
        <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
          Contact Details ({contacts.length})
        </h3>
        <p className="mt-1 text-xs text-neutral-500">
          Add multiple phone numbers, email addresses and physical addresses shown on the website.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr_140px_auto]">
          <select
            value={newContact.type}
            onChange={(e) => setNewContact((c) => ({ ...c, type: e.target.value }))}
            className="rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
          >
            {CONTACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {contactTypeLabel(t)}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={newContact.value}
            onChange={(e) => setNewContact((c) => ({ ...c, value: e.target.value }))}
            placeholder={newContact.type === "email" ? "sales@topgearautospares.com" : "+61 8 0000 0000"}
            className="rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
          />
          <input
            type="text"
            value={newContact.label}
            onChange={(e) => setNewContact((c) => ({ ...c, label: e.target.value }))}
            placeholder="Label (e.g. Workshop)"
            className="rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
          />
          <button
            type="button"
            onClick={addContact}
            className="flex items-center justify-center gap-2 rounded-md bg-brand-gold px-4 py-2.5 text-sm font-semibold text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add
          </button>
        </div>

        <ul className="mt-4 divide-y divide-neutral-100">
          {contacts.map((c) => (
            <li key={c.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600">
                  {contactTypeLabel(c.type)}
                </span>
                <span className="text-sm font-medium text-brand-dark">{c.value}</span>
                {c.label && <span className="text-xs text-neutral-500">({c.label})</span>}
              </div>
              <button
                onClick={() => removeContact(c.id)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-red-500 hover:bg-red-50"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          ))}
          {contacts.length === 0 && (
            <li className="py-6 text-center text-sm text-neutral-400">No contact details yet.</li>
          )}
        </ul>
      </section>
    </div>
  );
}